import { ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject, output, input, signal, linkedSignal } from '@angular/core';
import {Client, Package, Organization} from '@app/modules/shared/models';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import moment from 'moment-timezone';
import {TzDatePipe} from '@app/modules/shared/pipes/tz-date.pipe';
import {utmParamsValidator} from '@app/modules/shared/validators/tracked-url.validator';
import {
  ClientAdd,
  ClientGet,
  ClientUpdate,
  getClient,
  getClientError,
  getClientSuccess,
  getPackagesListAll, getPackagesListAllError,
  IClientState,
  IPackageState,
  IOrganizationState,
  ResetClientState,
  PackagesListAll,
  OrganizationsListAll,
  getOrganizationsListAll,
  IAuthenticationState,
  getLoggedInUser
} from '@app/stores';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import { Constants } from '@app/consts';
import { PageShellComponent } from '../../../_components/page-shell/page-shell.component';
import { SbInputFormComponent, SbInputFormActionsDirective } from '../../../_components/unified/sb-form/sb-form.component';
import { SbInputImageComponent } from '../../../_components/unified/sb-input/sb-input-image.component';
import { SbInputTimeComponent } from '../../../_components/unified/sb-input/sb-input-time.component';
import { CreatableSelectComponent } from '../../../_components/creatable-select/creatable-select.component';
import { InfoPopoverComponent } from '../../../_components/info-popover/info-popover.component';
import { SbButtonComponent } from '../../../_components/unified/sb-button/sb-button.component';

@Component({
  selector: 'app-client-add',
  templateUrl: './client-add.component.html',
  styleUrls: ['./client-add.component.css'],
  providers: [Constants],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PageShellComponent, SbInputFormComponent, ReactiveFormsModule, SbInputImageComponent, CreatableSelectComponent, SbInputTimeComponent, InfoPopoverComponent, SbInputFormActionsDirective, SbButtonComponent]
})
export class ClientAddComponent implements OnInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private clientStore = inject<Store<IClientState>>(Store);
  private packageStore = inject<Store<IPackageState>>(Store);
  private organizationStore = inject<Store<IOrganizationState>>(Store);
  private toastrService = inject(ToastrService);
  constants = inject(Constants);
  private tzDatePipe = inject(TzDatePipe);
  private authenticationStore = inject<Store<IAuthenticationState>>(Store);
  private cdr = inject(ChangeDetectorRef);


  public min: Date = new Date();
  public max: Date = this.min;
  public client: Client;
  readonly packages = signal<Package[]>([]);
  readonly organizations = signal<Organization[]>([]);
  addClientForm: ReturnType<ClientAddComponent['buildForm']>;
  unsubscriber = new Subject();
  readonly submitted = signal(false);
  /** Route param on the edit page, or input when opened as a modal. */
  readonly clientIdInput = input<number>(undefined, {alias: 'clientId'});
  readonly clientId = linkedSignal(() => this.clientIdInput());
  /** True when opened inside an NgbModal via EntityFormModalService. */
  readonly isModal = input(false);
  readonly modalDone = output<'saved' | 'cancelled'>();
  readonly currentUser = signal<any>(undefined);

  constructor() {
    this.clientStore.dispatch(ResetClientState({params: {error: '', success: '', client: null}}));
    this.subscribeStores();
  }

  subscribeStores() {
    this.authenticationStore.pipe(select(getLoggedInUser))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(user => {
        if (user) {
          this.currentUser.set(user);
        }
      });
    this.clientStore.pipe(select(getClientError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.toastrService.error(error);
        }
      });
    this.clientStore.pipe(select(getClientSuccess))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(success => {
        if (success) {
          if (this.submitted()) {
            this.submitted.set(false);
            if (this.isModal()) {
              this.modalDone.emit('saved');
              return;
            }
            this.toastrService.success(success);
            this.router.navigate(['/clients']);
          }
        }
      });
    this.packageStore.pipe(select(getPackagesListAll))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(data => {
        if (data) {
          this.packages.set(data);
        }
      });
    this.organizationStore.pipe(select(getOrganizationsListAll))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(data => {
        if (data) {
          this.organizations.set(data);
        }
      });
    this.clientStore.pipe(select(getClient))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(client => {
        if (client) {
          const clientObj =  Object.assign({}, client);
          let fromDate: any = `${(clientObj.package_start_date ? moment(clientObj.package_start_date).format('MM/DD/YYYY') : '') || ''} ${clientObj.package_start_date || ''}`;
          let toDate: any = `${(clientObj.package_end_date ? moment(clientObj.package_end_date).format('MM/DD/YYYY') : '') || ''} ${clientObj.package_end_date || ''}`;
          if (fromDate.trim()) {
            fromDate = this.tzDatePipe.toUTCMomentObject(moment(fromDate.trim(), `MM/DD/YYYY${clientObj.package_start_date ? ' h:m A' : ''}`));
            clientObj.package_start_date = clientObj.package_start_date ? fromDate.format('MM/DD/YYYY') : '';
          } else {
            fromDate = null;
            clientObj.package_start_date = '';
          }
          if (toDate.trim()) {
            toDate = this.tzDatePipe.toUTCMomentObject(moment(toDate.trim(), `MM/DD/YYYY${clientObj.package_end_date ? ' h:m A' : ''}`));
            clientObj.package_end_date = clientObj.package_end_date ? toDate.format('MM/DD/YYYY') : '';
          } else {
            toDate = null;
            clientObj.package_end_date = '';
          }
          // A package that started before today keeps its start date valid: the date picker checks
          // a loaded date against its minimum, which the old picker did only for a date picked or typed.
          const startDate = moment(clientObj.package_start_date, 'MM/DD/YYYY', true);
          if (startDate.isValid() && startDate.isBefore(this.min, 'day')) {
            this.min = startDate.toDate();
          }
          this.addClientForm.patchValue({...clientObj});
          const clientSettings = typeof clientObj.client_settings === 'string'
            ? JSON.parse(clientObj.client_settings)
            : (clientObj.client_settings || {});
          this.addClientForm.patchValue({
            widget_version: this.normalizeInheritedWidgetVersion(clientSettings.widget_version),
            widget_cdn_enabled: this.normalizeInheritedBoolean(clientSettings.widget_cdn_enabled)
          });
          // The patch also moves the end date's lower bound (max), through valueChanges.
          this.cdr.markForCheck();
        }
      });
  }

  ngOnInit(): void {
    this.addClientForm = this.buildForm();
    if (this.isModal()) {
      // Opened via EntityFormModalService: clientId comes from the input.
      const clientId = this.clientId();
      if (clientId) {
        this.loadClient(clientId);
      }
    } else {
      this.route.paramMap.subscribe(params => {
        if (params.has('id')) {
          this.clientId.set(+params.get('id'));
          this.cdr.markForCheck();
          this.loadClient(this.clientId());
        }
      });
    }
    this.addClientForm.get('package_start_date').valueChanges.subscribe(value => {
      this.max = value;
      // const endDate = this.addProjectForm.get('end_date').value;
      // if (value && endDate && Math.floor((Date.parse(value) - Date.parse(endDate)) / 86400000) > 0) {
      //   this.addProjectForm.patchValue({end_date: null, end_time: null});
      // }
    });
    this.packageStore.dispatch(PackagesListAll({}));
    this.organizationStore.dispatch(OrganizationsListAll({}));
  }

  private buildForm() {
    return this.formBuilder.group({
      name: ['', Validators.required],
      password: [Math.random().toString(36).slice(-8), Validators.required],
      email: ['', [Validators.required, Validators.email]],
      logo: [''],
      organization_id: [null as number | null],
      package_id: [null as number | null],
      package_start_date: [null as any],
      package_end_date: [null as any],
      website: ['', [Validators.required, Validators.pattern(this.constants.urlRegex)]],
      comment: [''],
      login_whitelisted_ips: [null as string | null],
      require_google_utm_for_cta_links: [true as boolean | number],
      google_utm_params: [null as any, utmParamsValidator()],
      widget_version: [null as any],
      widget_cdn_enabled: [null as any]
    });
  }

  get f() {
    return this.addClientForm.controls;
  }

  loadClient(clientId: number) {
    this.clientStore.dispatch(ClientGet({clientId}));
  }

  cancelClick() {
    if (this.isModal()) {
      this.modalDone.emit('cancelled');
      return;
    }
    this.router.navigate(['/clients']);
  }

  saveClient() {
    this.submitted.set(true);
    if (this.addClientForm.invalid) {
      return;
    }

    this.saveClientData();
  }

  saveClientData() {
    // Reshaped below (client_settings) before it is sent.
    const data: any = this.addClientForm.value;
    if (data.package_start_date === '') {
      data.package_start_date = null;
    }
    if (data.package_end_date === '') {
      data.package_end_date = null;
    }
    if (data.login_whitelisted_ips === '') {
      data.login_whitelisted_ips = null;
    }
    if (data.require_google_utm_for_cta_links === '') {
      data.require_google_utm_for_cta_links = 0;
    }
    if (data.google_utm_params === '') {
      data.google_utm_params = null;
    }

    data.client_settings = {
      widget_version: this.normalizeInheritedWidgetVersion(data.widget_version),
      widget_cdn_enabled: this.normalizeInheritedBoolean(data.widget_cdn_enabled)
    };
    delete data.widget_version;
    delete data.widget_cdn_enabled;

    const clientId = this.clientId();
    if (clientId) {
      this.clientStore.dispatch(ClientUpdate({clientId: clientId, client: data}));
    } else {
      this.clientStore.dispatch(ClientAdd({client: data}));
    }
  }

  generateRandomPassword() {
    this.addClientForm.patchValue({password: Math.random().toString(36).slice(-8)});
  }

  packageSelected(e) {
    this.addClientForm.patchValue({package_id: e?.value || null});
  }

  organizationSelected(e) {
    this.addClientForm.patchValue({organization_id: e?.value || null});
  }

  private normalizeInheritedBoolean(value: any): boolean | null {
    if (value === true || value === 'true') {
      return true;
    }
    if (value === false || value === 'false') {
      return false;
    }
    return null;
  }

  private normalizeInheritedWidgetVersion(value: any): 'v1' | 'v2' | null {
    return value === 'v1' || value === 'v2' ? value : null;
  }

  ngOnDestroy() {
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
  }

}
